import { z } from "zod";
import { REPORT_REASONS, REPORT_STATUSES, type ReportReason } from "../domain/admin";

/**
 * What a reporter may attach as evidence: a screenshot, or a screen recording.
 *
 * ⚠️ `video/quicktime` IS iOS — an iPhone screen recording is `.mov`, so leaving it out would make
 * the feature unusable on half the platform. `image/webp` is here because some Android screenshot
 * tools emit it; the client re-encodes what it can and passes through what it cannot.
 */
export const REPORT_ATTACHMENT_MIME_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "video/mp4",
  "video/quicktime",
  "video/webm",
] as const;
export type ReportAttachmentMime = (typeof REPORT_ATTACHMENT_MIME_TYPES)[number];

export function isReportAttachmentMime(value: string): value is ReportAttachmentMime {
  return (REPORT_ATTACHMENT_MIME_TYPES as readonly string[]).includes(value);
}

/**
 * The ceiling on an attachment, in bytes.
 *
 * ⚠️ THIS NUMBER IS LOAD-BEARING IN THREE PLACES AND THEY MUST NOT DRIFT: the web refuses a bigger
 * pick, the api route's `bodyLimit` rejects a bigger body, and **nginx's `client_max_body_size` must
 * exceed it** — the whole avatar-upload outage was a client that accepted 5 MB while nginx capped at
 * 2 MB, so the 413 came from a layer with no Persian error to show. nginx is set to `6m` on our five
 * vhosts to leave headroom over this. Raising this constant means raising nginx FIRST.
 */
export const MAX_REPORT_ATTACHMENT_BYTES = 5 * 1024 * 1024;

/** What `POST /reports/attachment` hands back — the handle the report then references. */
export const reportAttachmentResponseSchema = z.object({ attachmentId: z.string().min(1) });
export type ReportAttachmentResponse = z.infer<typeof reportAttachmentResponseSchema>;

/**
 * Where an attachment lives between upload and forwarding: Redis, not Postgres.
 *
 * The bytes only need to survive the gap between the reporter pressing Submit and the bot's 15 s
 * forwarder relaying them — after that Telegram holds the copy the moderators actually look at. A
 * `Bytes` column would keep every screenshot and screen recording forever for no reader, so this is
 * a self-expiring key instead: **no blob in Postgres, and no migration** (the key is derived from
 * the report id, so the `Report` row needs no new column).
 *
 * The api writes it under a fresh `attachmentId`, then RENAMEs it onto the report id at submit;
 * the bot reads and deletes it when it forwards. An hour is far more than the 15 s poll needs — the
 * slack is there so a forwarder that is retrying a failed send does not lose the evidence.
 */
export const REPORT_ATTACHMENT_TTL_SEC = 3600;

/** Up to three files per report — enough to show a short exchange, few enough to stay one album. */
export const MAX_REPORT_ATTACHMENTS = 3;

/**
 * The Redis key. An UPLOAD is parked under its own handle (`index` omitted); submitting the report
 * re-keys each one onto `report:attach:<reportId>:<i>`, which is where the bot looks — so the
 * `Report` row still needs no column and there is still no migration.
 */
export function reportAttachmentKey(id: string, index?: number): string {
  return index === undefined ? `report:attach:${id}` : `report:attach:${id}:${index}`;
}

/**
 * WHICH REASONS REQUIRE EVIDENCE — and why it is not simply "all of them".
 *
 * A screenshot is demanded only where a single frame actually PROVES the thing being reported:
 * an insult or a piece of inappropriate content is visible in one image, and a moderator can act
 * on it directly.
 *
 * ⚠️ CHEATING AND SPAM ARE DELIBERATELY EXEMPT. Both are BEHAVIOURAL PATTERNS — a run of hands, a
 * stream of messages — that no one frame captures, so requiring a screenshot there would not
 * produce better evidence; it would just block legitimate reports. That matters concretely here:
 * uploads on this platform's networks were measured at **25.4% failure for a ~40 KB body** on
 * 2026-08-08, and a screenshot is far larger, so every reason marked mandatory is a reason some
 * users cannot report at all.
 *
 * ⚠️ `OTHER` IS OPTIONAL BY THE SAME REASONING (assumption stated to the operator, 2026-08-08): it
 * is a catch-all, so we cannot know a screenshot proves anything about it, and the cost of being
 * wrongly-mandatory is a report that never gets filed.
 */
export const REPORT_REASONS_REQUIRING_ATTACHMENT = ["ABUSE", "INAPPROPRIATE"] as const;

export function reportRequiresAttachment(reason: ReportReason): boolean {
  return (REPORT_REASONS_REQUIRING_ATTACHMENT as readonly string[]).includes(reason);
}

/**
 * A user reporting another user.
 *
 * ⚠️ `attachmentId` IS CONDITIONALLY REQUIRED — see {@link reportRequiresAttachment}. The client
 * keeps Submit disabled until the upload has COMPLETED for the reasons that need one; this schema
 * is the server-side half of that guarantee, so a crafted request cannot skip it.
 *
 * An attachment sent for an EXEMPT reason is kept and forwarded — evidence is welcome everywhere,
 * it is only *demanded* where it proves something.
 */
export const submitReportSchema = z
  .object({
    reason: z.enum(REPORT_REASONS),
    details: z.string().trim().max(1000).optional(),
    attachmentIds: z.array(z.string().min(1)).max(MAX_REPORT_ATTACHMENTS).optional(),
  })
  .refine(
    (input) => !reportRequiresAttachment(input.reason) || (input.attachmentIds?.length ?? 0) > 0,
    {
      message: "This report reason requires a screenshot or screen recording",
      path: ["attachmentIds"],
    },
  );
export type SubmitReportInput = z.infer<typeof submitReportSchema>;

/**
 * Admin-facing report row. Identities are resolved to DISPLAY NAMES (never a Telegram id / handle
 * — the same leak-free contract as everywhere else); the ids are the app cuids for admin actions.
 */
export const reportViewSchema = z.object({
  id: z.string(),
  reason: z.enum(REPORT_REASONS),
  details: z.string().nullable(),
  status: z.enum(REPORT_STATUSES),
  reporterId: z.string(),
  reporterName: z.string(),
  targetUserId: z.string().nullable(),
  targetName: z.string().nullable(),
  createdAt: z.string(),
  resolvedAt: z.string().nullable(),
});
export type ReportView = z.infer<typeof reportViewSchema>;

/** Admin resolving a report: RESOLVED = actioned, DISMISSED = no action needed. */
export const resolveReportSchema = z.object({
  status: z.enum(["RESOLVED", "DISMISSED"]),
});
export type ResolveReportInput = z.infer<typeof resolveReportSchema>;
